import {
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgModel,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TypeaheadMatch, TypeaheadOrder } from 'ngx-bootstrap/typeahead';
import { RRIDropdownOptionTemplate } from '../../interfaces/dropdown-option-template.interface';
import { RRIInputOption } from '../../interfaces/input-option.interface';
import { RRIInput } from '../../interfaces/input-settings.interface';

@Component({
  selector: 'rr-dropdown-field',
  templateUrl: './dropdown-field.component.html',
  styleUrls: ['./dropdown-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DropdownFieldComponent),
      multi: true,
    },
  ],
})
export class DropdownFieldComponent<T>
  implements OnInit, ControlValueAccessor, Validator
{
  public selectedOption: RRIInputOption<T>;
  public sortConfig: TypeaheadOrder = {
    direction: 'asc',
    field: 'value',
  };
  public data: RRIInputOption<T> = null;
  public onChangeFn: any = (_: any) => {};
  public onTouchFn: any = () => {};
  public control: NgModel;
  public noResult = false;
  public iconClassName = 'info-icon-two-action-buttons';
  private errorMessages = new Map<string, () => string>();

  @Input() sort: 'asc' | 'desc' | 'none';
  @Input() settings: RRIInput<RRIInputOption<T>>;
  @Input() options: RRIInputOption<T>[];
  @Input() optionsTemplate: TemplateRef<RRIDropdownOptionTemplate<string>>;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() customErrorMessages: Map<string, () => string>;
  @Input() allowCustomInput: boolean = false;
  @Output() clearInput: EventEmitter<boolean> = new EventEmitter();
  @Output() valueChanged: EventEmitter<RRIInputOption<T>> = new EventEmitter();

  @ViewChild('dropdownInput') dropdownInput: ElementRef;

  constructor(private inj: Injector) {
    this.errorMessages.set(
      'required',
      () => `${this.settings.label} is required.`,
    );
  }

  ngOnInit(): void {
    this.control = this.inj.get(NgModel, null);
    if (this.customErrorMessages && this.customErrorMessages.size) {
      this.errorMessages = new Map([
        ...Array.from(this.errorMessages.entries()),
        ...Array.from(this.customErrorMessages.entries()),
      ]);
    }
    if (this.sort && this.sort === 'none') {
      this.sortConfig = null;
    }
    if (this.sort && (this.sort === 'asc' || this.sort === 'desc')) {
      this.sortConfig.direction = this.sort;
    }
    if (this.settings?.hideResetInput) {
      this.iconClassName = 'info-icon-no-clear-two-action-buttons';
    }
  }

  public changeSelectedFilter(event: TypeaheadMatch): void {
    if (
      event &&
      event.item &&
      (!this.data || (this.data && this.data.id !== event.item.id))
    ) {
      this.data = event.item;
    }
    this.valueChanged.emit(this.data);
    this.onChange();
  }

  public clickedSelectedFilter(): void {
    if (this.data && (`${this.data.id}` === '0' || this.data.id)) {
      this.selectedOption = this.options.find((x) => x.id === this.data.id);
    } else if (this.allowCustomInput && !this.data.id && this.data.value) {
      this.selectedOption = { id: '', value: this.data.value };
    }
    if (!(this.allowCustomInput && !this.data.id && this.data.value)) {
      this.data = null;
    }
    this.onChange();
  }

  public clickedDropdown(): void {
    if (this.data && (`${this.data.id}` === '0' || this.data.id)) {
      this.selectedOption = this.options.find((x) => x.id === this.data.id);
    } else if (this.allowCustomInput && !this.data.id && this.data.value) {
      this.selectedOption = { id: '', value: this.data.value };
    }
    this.data = null;
    this.dropdownInput.nativeElement.click();
    this.onChange();
  }

  public typeaheadOnBlur(event): void {
    if (
      !this.data ||
      (this.data.id === '' &&
        typeof this.data.value === 'string' &&
        this.data.value === '') ||
      (this.selectedOption &&
        this.selectedOption.id !== this.data.id &&
        this.noResult &&
        !this.allowCustomInput)
    ) {
      this.data = { ...this.selectedOption };

      if (!this.selectedOption && this.allowCustomInput) {
        this.data = { id: event.value, value: event.value };
      }
    }

    this.valueChanged.emit(this.data);
    this.onChange();
  }

  public changeOnNoResults(event: boolean) {
    this.noResult = event;
  }

  public onClearInput(): void {
    if (!this.settings.disableClearEvent) {
      if (this.settings.defaultValue) {
        this.data = this.settings.defaultValue;
        this.selectedOption = this.options.find((x) => x.id === this.data.id);
      } else {
        this.data = null;
        this.selectedOption = null;
      }
    }

    this.clearInput.emit(true);
    this.onChange();
  }

  public writeValue(value: RRIInputOption<T>): void {
    this.data = value;
  }

  public registerOnChange(fn: RRIInputOption<string>): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchFn = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onChange() {
    this.onChangeFn(this.data);
  }

  public get invalid(): boolean {
    return this.control ? this.control.invalid : false;
  }

  public get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, value } = this.control;

    return this.invalid ? dirty || touched || !!value : false;
  }

  public get errors(): Array<string> {
    if (!this.control) {
      return [];
    }

    const { errors } = this.control;
    return Object.keys(errors).map((key) => {
      return this.errorMessages.has(key)
        ? this.errorMessages.get(key)()
        : 'error: ' + key;
    });
  }

  public validate(_control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
